import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyGamesState } from '@/components/features/games/EmptyGamesState';
import { GameCardSkeleton } from '@/components/features/games/GameCardSkeleton';
import { GameFormSheet } from '@/components/features/games/GameFormSheet';
import { GameCard } from '@/components/shared/cards';
import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { deleteGameDefinition, restoreGameDefinition } from '@/db/game-definitions';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useGameDefinitions } from '@/hooks/useGameDefinitions';
import { useInitialSync } from '@/hooks/useInitialSync';
import { useRouteAnimation } from '@/hooks/useRouteAnimation';
import { handleUserFacingError } from '@/lib/error-handler';
import { slideUp, staggerConfig } from '@/lib/motion-config';
import { toastDeleteWithUndo, toastError } from '@/lib/toast-helpers';
import { parseGameDefinitionId, type GameDefinition } from '@/schemas';

export const Route = createFileRoute('/games/')({
  component: GamesPage,
});

function GamesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('games');
  const { t: tCommon } = useTranslation('common');
  const { isSyncing } = useInitialSync();
  const {
    gameDefinitions: games,
    isLoading: isLoadingGames,
    hasData: hasGameData,
    refresh: refreshGames,
  } = useGameDefinitions();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'presets' | 'custom'>('all');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingGameId, setEditingGameId] = useState<string | undefined>();

  useDocumentTitle(`${t('title', 'Games')} - ${tCommon('app.name', 'One Score')}`);

  const showInitialSkeleton = (isSyncing || isLoadingGames) && !hasGameData;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchInput]);

  // Separate presets from custom games
  const { presetGames, customGames } = useMemo(() => {
    const presets: GameDefinition[] = [];
    const custom: GameDefinition[] = [];

    for (const game of games) {
      const isPreset =
        (game as GameDefinition & { isSystemPreset?: boolean }).isSystemPreset === true;
      if (isPreset) presets.push(game);
      else custom.push(game);
    }

    return {
      presetGames: presets.toSorted((a, b) => a.name.localeCompare(b.name)),
      customGames: custom.toSorted(
        (a, b) => (b.updatedAt ?? b.createdAt) - (a.updatedAt ?? a.createdAt),
      ),
    };
  }, [games]);

  // Filter games based on search and tab
  const filteredGames = useMemo(() => {
    let toFilter: GameDefinition[];
    if (activeTab === 'presets') {
      toFilter = presetGames;
    } else if (activeTab === 'custom') {
      toFilter = customGames;
    } else {
      toFilter = games;
    }

    if (!debouncedSearch.trim()) {
      return toFilter;
    }

    const searchLower = debouncedSearch.toLowerCase();
    return toFilter.filter(
      (game) =>
        game.name.toLowerCase().includes(searchLower) ||
        game.description?.toLowerCase().includes(searchLower),
    );
  }, [activeTab, presetGames, customGames, games, debouncedSearch]);

  const handleDeleteClick = (gameId: string) => {
    setConfirmDeleteId(gameId);
    setDeleteError(undefined);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteId) {
      return;
    }

    const brandedGameId = parseGameDefinitionId(confirmDeleteId);

    // Protect default game and presets from deletion
    const game = games.find((g) => String(g.id) === confirmDeleteId);
    if (game && (game as GameDefinition & { isSystemPreset?: boolean }).isSystemPreset) {
      setDeleteError(t('deleteConfirm.preset', 'Cannot delete preset games'));
      setConfirmDeleteId(undefined);
      return;
    }

    setIsDeleting(true);
    setDeleteError(undefined);

    try {
      await deleteGameDefinition(brandedGameId);
      toastDeleteWithUndo(
        tCommon('games.deleteSuccess', 'Game deleted successfully'),
        async () => {
          await restoreGameDefinition(brandedGameId);
          await refreshGames();
        },
        tCommon('undo', 'undo'),
      );
      setConfirmDeleteId(undefined);
      await refreshGames();
    } catch (error) {
      const errorMessage = handleUserFacingError(
        error,
        t('games.deleteError', 'Failed to delete game'),
        { operation: 'deleteGameDefinition', gameId: confirmDeleteId },
      );
      setDeleteError(errorMessage);
      toastError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDeleteId(undefined);
    setDeleteError(undefined);
  };

  const handleEditClick = (gameId: string) => {
    setEditingGameId(gameId);
    setSheetOpen(true);
  };

  const handleCreateClick = () => {
    setEditingGameId(undefined);
    setSheetOpen(true);
  };

  const handleSheetSuccess = useCallback(() => {
    refreshGames().catch((error_) => {
      handleUserFacingError(error_, 'Failed to refresh games', {
        operation: 'refreshGames',
      });
    });
  }, [refreshGames]);

  const handleStartSessionWithPreset = useCallback(
    (preset: GameDefinition) => {
      const presetConfig = {
        hasRounds: preset.hasRounds,
        winCondition: preset.winCondition,
        roundTargetPoints: preset.roundTargetPoints,
        gameTargetPoints: preset.gameTargetPoints,
        targetRounds: preset.targetRounds,
        teamScoringType: preset.teamScoringType,
        version: preset.version ?? 1,
      };

      navigate({
        to: '/sessions/new',
        state: (previous) => ({
          ...(typeof previous === 'object' && previous ? previous : {}),
          gameConfig: presetConfig,
          selectedPreset: preset,
        }),
      }).catch(() => {
        // Ignore navigation errors
      });
    },
    [navigate],
  );

  const showCustomEmptyState =
    !showInitialSkeleton && activeTab === 'custom' && customGames.length === 0;
  const showGlobalEmptyState = !showInitialSkeleton && games.length === 0;
  // Show "New Game" button if there are ANY games (unfiltered), regardless of current tab
  // Note: hasGameData is already games.length > 0, so this checks unfiltered data
  const shouldShowHeaderCreateButton = !showInitialSkeleton && hasGameData;

  const { shouldAnimate, routeKey } = useRouteAnimation();

  return (
    <div className="w-full max-w-6xl space-y-6 px-4 mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('title', 'Games')}</h1>
          <p className="text-muted-foreground">
            {t('description', 'Manage your game definitions')}
          </p>
        </div>
        {shouldShowHeaderCreateButton && (
          <Button
            className="w-full sm:w-auto"
            onClick={handleCreateClick}
            data-testid="create-game-button"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('createButton', 'Create Game')}
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      {(games.length >= 6 || filteredGames.length >= 6) && !showInitialSkeleton && (
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="relative w-full sm:flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={
                !showInitialSkeleton && games.length > 0
                  ? (() => {
                      const hasActiveFilters =
                        activeTab !== 'all' || debouncedSearch.trim().length > 0;
                      if (hasActiveFilters) {
                        return t('searchWithCountFiltered', {
                          count: filteredGames.length,
                          filtered: filteredGames.length,
                          total: games.length,
                        });
                      }
                      return t('searchWithCount', { count: games.length });
                    })()
                  : t('search', 'Search games...')
              }
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
              aria-label={t('search', 'Search games...')}
            />
          </div>
          <Tabs
            className="w-full sm:w-auto"
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as typeof activeTab)}
          >
            <TabsList className="flex w-full flex-wrap gap-1 sm:w-auto">
              <TabsTrigger value="all">{tCommon('all', 'All')}</TabsTrigger>
              <TabsTrigger value="presets">{t('preset', 'Preset Games')}</TabsTrigger>
              <TabsTrigger value="custom">{t('custom', 'Custom Games')}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Loading State */}
      {showInitialSkeleton ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <GameCardSkeleton key={i} />
          ))}
        </div>
      ) : (
        <>
          {/* Empty State for Custom Games Tab */}
          {showCustomEmptyState && <EmptyGamesState onCreateClick={handleCreateClick} />}

          {/* Games Grid */}
          {filteredGames.length > 0 && (
            <motion.div
              key={`games-grid-${routeKey}`}
              className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
              variants={slideUp}
              initial={shouldAnimate ? 'initial' : false}
              animate="animate"
              transition={staggerConfig.container}
              data-testid="game-list"
            >
              <AnimatePresence mode="popLayout">
                {filteredGames.map((game) => {
                  const isSystemPreset = (game as GameDefinition & { isSystemPreset?: boolean })
                    .isSystemPreset;
                  const isDefault = false;

                  return (
                    <motion.div
                      key={game.id}
                      layout
                      layoutId={`game-card-${game.id}`}
                      variants={slideUp}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                    >
                      <GameCard
                        game={game as GameDefinition & { isSystemPreset?: boolean }}
                        onEdit={() => handleEditClick(String(game.id))}
                        onDelete={() => handleDeleteClick(String(game.id))}
                        onStartSession={() => handleStartSessionWithPreset(game)}
                        canEdit={true} // Allow editing all games including default
                        canDelete={!isSystemPreset && !isDefault} // Only prevent deleting default and system presets
                        isDefault={isDefault}
                        data-testid={`game-card-${game.id}`}
                      />
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          )}

          {/* No Results */}
          {!showInitialSkeleton && filteredGames.length === 0 && games.length > 0 && (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Search className="h-8 w-8" />
                </EmptyMedia>
                <EmptyTitle>
                  {tCommon('games.noResults', 'No games found matching your search')}
                </EmptyTitle>
                <EmptyDescription>
                  {tCommon(
                    'games.noResultsDescription',
                    "Try adjusting your search terms or filters to find what you're looking for.",
                  )}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}

          {/* No Games at All */}
          {showGlobalEmptyState && <EmptyGamesState onCreateClick={handleCreateClick} />}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={!!confirmDeleteId}
        onOpenChange={(open) => {
          if (!open) {
            handleDeleteCancel();
          }
        }}
        title={t('deleteConfirm.title', 'Delete game?')}
        description={
          deleteError || t('deleteConfirm.description', "This won't affect existing sessions.")
        }
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
        error={deleteError}
        confirmLabel={t('deleteConfirm.confirm', 'Delete')}
      />

      {/* Game Form Sheet */}
      <GameFormSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        gameId={editingGameId}
        onSuccess={handleSheetSuccess}
      />
    </div>
  );
}
